<template lang="pug">
#skills
    v-container
        div(v-bind:style="containerStyle")
            div(
                v-for="(column, columnIndex) in columns"
                v-bind:key="columnIndex"
                v-bind:class="layoutOptions.columnClass"
                v-bind:style="columnStyle"
            )
                ExpandableBox(v-for="(skill, skillIndex) in column" v-bind:key="skillIndex" v-bind:item="skill")
</template>
<script lang="ts">
import ExpandableBox, { BoxItem } from '../components/ExpandableBox.vue';

export interface LayoutOptions {
    gutter: { [key: string]: string | number };
    cols: { [key: string]: string | number };
    columnClass: string;
}

export default {
    name: 'Skills',
    components: {
        ExpandableBox,
    },
    props: {
        layoutOptions: Object as () => LayoutOptions,
        skills: Array as () => BoxItem[],
    },
    data(): Record<string, unknown> {
        return {
            windowWidth: window.innerWidth,
        };
    },
    computed: {
        displayColumns(): number {
            return Math.max(1, Number(this.breakpointValue(this.layoutOptions.cols, this.windowWidth as number)) || 1);
        },
        displayGutter(): string | number {
            return this.breakpointValue(this.layoutOptions.gutter, this.windowWidth as number);
        },
        gutterSizeWithUnit(): string {
            const gutter = String(this.displayGutter);
            return /^\d+(\.\d+)?$/.test(gutter) ? `${gutter}px` : gutter;
        },
        containerStyle(): Record<string, unknown> {
            return {
                display: 'flex',
                marginLeft: `-${this.gutterSizeWithUnit}`,
            };
        },
        columnStyle(): Record<string, unknown> {
            return {
                boxSizing: 'border-box',
                backgroundClip: 'padding-box',
                width: `${100 / this.displayColumns}%`,
                border: '0 solid transparent',
                borderLeftWidth: this.gutterSizeWithUnit,
            };
        },
        columns(): BoxItem[][] {
            const cols: BoxItem[][] = [];
            this.skills.forEach((skill, index) => {
                const columnIndex = index % this.displayColumns;
                if (!cols[columnIndex]) {
                    cols[columnIndex] = [];
                }
                cols[columnIndex].push(skill);
            });
            return cols;
        },
    },
    methods: {
        breakpointValue(
            mixed: string | number | { [key: string]: string | number },
            windowWidth: number
        ): string | number {
            const valueAsNum = parseInt(String(mixed));
            if (valueAsNum > -1) {
                return mixed;
            }
            if (typeof mixed !== 'object') {
                return 0;
            }
            let matchedBreakpoint = Infinity;
            let matchedValue = mixed.default || 0;
            for (const key in mixed) {
                const breakpoint = parseInt(key);
                const breakpointValRaw = mixed[breakpoint];
                const breakpointVal = parseInt(String(breakpointValRaw));
                if (isNaN(breakpoint) || isNaN(breakpointVal)) {
                    continue;
                }
                if (windowWidth <= breakpoint && breakpoint < matchedBreakpoint) {
                    matchedBreakpoint = breakpoint;
                    matchedValue = breakpointValRaw;
                }
            }
            return matchedValue;
        },
        reCalculate(): void {
            this.windowWidth = window.innerWidth || Infinity;
        },
    },
    mounted(): void {
        this.reCalculate();
        window.addEventListener('resize', this.reCalculate);
    },
    beforeUnmount(): void {
        window.removeEventListener('resize', this.reCalculate);
    },
};
</script>
<style lang="scss" scoped>
.box {
    border-radius: 10px;
    background-color: #303030;
    color: white;
}
</style>
